import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  WrenchIcon,
  KeyIcon,
  ShieldCheckIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";

/**
 * Login support tool — paste the user's email, see exactly why their login
 * is failing (user not found / banned / Google-only / wrong password), and
 * one-click force-reset their password to a new temporary value the team
 * can hand off.
 *
 * Mounts inside the main admin dashboard at /admin#login-support.
 */
export default function AdminLoginSupport() {
  const [email, setEmail] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [resetting, setResetting] = useState(false);

  const lookup = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setData(null);
    setResetResult(null);
    try {
      const res = await api.get(
        `/admin/login-debug?email=${encodeURIComponent(email.trim())}`
      );
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const forceReset = async () => {
    if (!data?.user?.user_id) return;
    if (!window.confirm(`Force-reset password for ${data.user.email}? They'll need a new temp password to log in.`)) return;
    setResetting(true);
    try {
      const res = await api.post(`/admin/users/${data.user.user_id}/reset-password`);
      setResetResult(res.data);
      toast.success("Password reset. Hand the temp password to the user securely.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setResetting(false);
    }
  };

  const reasonHint = (reason) => {
    switch (reason) {
      case "user_not_found":
        return "No user exists with this email";
      case "wrong_password":
        return "Password didn't match the stored hash";
      case "no_password_hash":
        return "Account has no password (Google-only or corrupted)";
      default:
        return reason || "—";
    }
  };

  return (
    <div
      className="mt-10 border border-zinc-200 bg-white rounded-2xl overflow-hidden"
      id="login-support"
    >
      <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
        <WrenchIcon size={20} className="text-[#1D78AB]" />
        <div>
          <div className="font-display font-bold">Login support</div>
          <div className="text-xs text-zinc-500 mt-0.5 font-mono">
            Paste a user email to triage "I can't log in" tickets
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex gap-2">
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookup()}
            placeholder="user@email.com"
            className="h-10"
            data-testid="login-support-email-input"
          />
          <Button
            onClick={lookup}
            disabled={loading || !email.trim()}
            className="bg-[#1D78AB] hover:bg-[#176089] text-white"
            data-testid="login-support-lookup-btn"
          >
            {loading ? "Looking up…" : "Look up"}
          </Button>
        </div>

        {data && (
          <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                {data.found ? (
                  <ShieldCheckIcon size={20} className="text-emerald-600" />
                ) : (
                  <WarningCircleIcon size={20} className="text-red-600" />
                )}
                <span className="font-display font-bold">
                  {data.found ? "Account found" : "No account with this email"}
                </span>
              </div>
              {data.user?.user_id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={forceReset}
                  disabled={resetting}
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  data-testid="login-support-reset-btn"
                >
                  <KeyIcon size={14} className="mr-1" />
                  {resetting ? "Resetting…" : "Force-reset password"}
                </Button>
              )}
            </div>

            {data.user && (
              <ul className="text-sm space-y-1 font-mono">
                <li>
                  <span className="text-zinc-500">user_id:</span>{" "}
                  <code className="text-zinc-800">{data.user.user_id}</code>
                </li>
                <li>
                  <span className="text-zinc-500">email (stored):</span>{" "}
                  <code className="text-zinc-800">{data.user.email}</code>
                </li>
                <li>
                  <span className="text-zinc-500">username:</span>{" "}
                  @{data.user.username || "—"}
                </li>
                <li>
                  <span className="text-zinc-500">auth_provider:</span>{" "}
                  {data.user.auth_provider || "email"}
                </li>
                <li>
                  <span className="text-zinc-500">has_password:</span>{" "}
                  {data.user.has_password ? "yes" : <span className="text-red-600 font-bold">NO — Google-only</span>}
                </li>
                <li>
                  <span className="text-zinc-500">verification_status:</span>{" "}
                  {data.user.verification_status || "—"}
                </li>
                <li>
                  <span className="text-zinc-500">banned:</span>{" "}
                  {data.user.banned_at ? (
                    <span className="text-red-600 font-bold">
                      YES — {data.user.banned_reason || "no reason given"}
                    </span>
                  ) : (
                    "no"
                  )}
                </li>
              </ul>
            )}

            {(data.recent_failed_attempts?.length ?? 0) > 0 && (
              <div>
                <p className="overline text-zinc-500 mb-1.5">RECENT FAILED ATTEMPTS</p>
                <ul className="text-xs space-y-1 font-mono">
                  {data.recent_failed_attempts.map((a, i) => (
                    <li key={i} className="text-zinc-600">
                      {a.last_attempt} · count={a.count} · reason={reasonHint(a.reason)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {resetResult && (
              <div className="border border-amber-300 bg-amber-50 rounded-lg p-3">
                <div className="text-xs font-bold uppercase tracking-wide text-amber-800 mb-1">
                  Temporary password
                </div>
                <code
                  className="block bg-white rounded p-2 text-sm font-mono select-all break-all"
                  data-testid="login-support-temp-password"
                >
                  {resetResult.temporary_password}
                </code>
                <p className="text-xs text-amber-700 mt-2 leading-relaxed">
                  Share with the user via a secure channel (phone call, in-person).
                  They'll be prompted to change it on next login.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
